// src/context/AuthContext.jsx
import { createContext, useEffect, useRef, useState } from "react";
import { loginUser, logoutUser, getUserInfo, refreshSession } from "../api";
import {
  redirectToLoginWithExpiredSession,
  setDisplayAccountType,
} from "../script";

const AuthContext = createContext();
const SESSION_REFRESH_COOLDOWN_MS = 60 * 1000;
const SESSION_ACTIVITY_EVENTS = [
  "click",
  "keydown",
  "pointerdown",
  "scroll",
  "touchstart",
  "focus",
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastSessionRefreshRef = useRef(0);
  const refreshInFlightRef = useRef(false);

  const loadUser = async () => {
    try {
      const info = await getUserInfo();
      setDisplayAccountType(info?.account_type);
      setUser(info);
    } catch (err) {
      if (err?.response?.status === 401) {
        redirectToLoginWithExpiredSession();
      }
      console.warn("Failed to load user", err);
      setDisplayAccountType("credits");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    lastSessionRefreshRef.current = Date.now();

    const refreshAuthSession = async () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      const now = Date.now();
      if (
        refreshInFlightRef.current ||
        now - lastSessionRefreshRef.current < SESSION_REFRESH_COOLDOWN_MS
      ) {
        return;
      }

      refreshInFlightRef.current = true;

      try {
        await refreshSession();
        lastSessionRefreshRef.current = now;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          redirectToLoginWithExpiredSession();
          setUser(null);
        } else {
          console.warn("Failed to refresh session cookie", err);
        }
      } finally {
        refreshInFlightRef.current = false;
      }
    };

    const handleActivity = () => {
      void refreshAuthSession();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleActivity();
      }
    };

    SESSION_ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, true);
    });
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      SESSION_ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity, true);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user]);

  const login = async (email, password) => {
    await loginUser(email, password);
    return loadUser();
  };

  const logout = async () => {
    await logoutUser();
    setDisplayAccountType("credits");
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, login, logout, loading, refreshUser: loadUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
